import React, { useState, useEffect } from 'react';
import { useForm } from '../../context/FormContext';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { AdditionalIncomeSection } from './AdditionalIncomeSection';

// UK PAYE Tax + NI calculation (2025/26 rates) - matches AutoConvert
const calculateNetFromGrossAnnual = (grossAnnual) => {
  if (!grossAnnual || grossAnnual <= 0) return 0;
  const annual = parseFloat(grossAnnual);
  
  // Personal Allowance
  const personalAllowance = 12570;
  // Basic rate threshold
  const basicRateThreshold = 50270;
  
  // Income Tax
  let incomeTax = 0;
  if (annual > personalAllowance) {
    const taxableAtBasic = Math.min(annual, basicRateThreshold) - personalAllowance;
    incomeTax += taxableAtBasic * 0.20;
    
    if (annual > basicRateThreshold) {
      const taxableAtHigher = annual - basicRateThreshold;
      incomeTax += taxableAtHigher * 0.40;
    }
  }
  
  // National Insurance (Class 1)
  let nationalInsurance = 0;
  if (annual > personalAllowance) {
    const niableAtMain = Math.min(annual, basicRateThreshold) - personalAllowance;
    nationalInsurance += niableAtMain * 0.08;
    
    if (annual > basicRateThreshold) {
      const niableAtHigher = annual - basicRateThreshold;
      nationalInsurance += niableAtHigher * 0.02;
    }
  }
  
  const netAnnual = annual - incomeTax - nationalInsurance;
  return Math.round(netAnnual / 12);
};

const calculateGrossMonthly = (grossAnnual) => {
  if (!grossAnnual || grossAnnual <= 0) return 0;
  return Math.round(parseFloat(grossAnnual) / 12);
};

// Reverse: estimate gross annual from net monthly
const estimateGrossFromNet = (netMonthly) => {
  if (!netMonthly || netMonthly <= 0) return 0;
  const targetNet = parseFloat(netMonthly) * 12;
  // Binary search for gross that produces this net
  let low = targetNet;
  let high = targetNet * 2;
  for (let i = 0; i < 50; i++) {
    const mid = (low + high) / 2;
    const calcNet = mid - calculateTaxAndNI(mid);
    if (calcNet < targetNet) low = mid;
    else high = mid;
  }
  return Math.round((low + high) / 2);
};

const calculateTaxAndNI = (annual) => {
  const personalAllowance = 12570;
  const basicRateThreshold = 50270;
  let tax = 0, ni = 0;
  if (annual > personalAllowance) {
    const basic = Math.min(annual, basicRateThreshold) - personalAllowance;
    tax += basic * 0.20;
    ni += basic * 0.08;
    if (annual > basicRateThreshold) {
      const higher = annual - basicRateThreshold;
      tax += higher * 0.40;
      ni += higher * 0.02;
    }
  }
  return tax + ni;
};

export const IncomeStep = () => {
  const { formData, updateFormData } = useForm();
  const [adjustManually, setAdjustManually] = useState(false);

  // Auto-calculate when gross_annual_income is set (e.g., from extraction) and other fields are empty
  useEffect(() => {
    if (!adjustManually && formData.gross_annual_income && 
        (!formData.gross_monthly_income || formData.gross_monthly_income === '0') &&
        (!formData.monthly_income || formData.monthly_income === '0')) {
      const annual = parseFloat(formData.gross_annual_income) || 0;
      if (annual > 0) {
        const grossMonthly = calculateGrossMonthly(annual);
        const netMonthly = calculateNetFromGrossAnnual(annual);
        updateFormData({
          gross_monthly_income: grossMonthly.toString(),
          monthly_income: netMonthly.toString()
        });
      }
    }
  }, [formData.gross_annual_income, adjustManually]);

  const handleGrossAnnualChange = (value) => {
    const cleanValue = value.replace(/[^\d.]/g, '');
    
    if (!adjustManually && cleanValue) {
      const annual = parseFloat(cleanValue) || 0;
      const grossMonthly = calculateGrossMonthly(annual);
      const netMonthly = calculateNetFromGrossAnnual(annual);
      
      updateFormData({
        gross_annual_income: cleanValue,
        gross_monthly_income: grossMonthly.toString(),
        monthly_income: netMonthly.toString()
      });
    } else {
      updateFormData({ gross_annual_income: cleanValue });
    }
  };

  const handleGrossMonthlyChange = (value) => {
    const cleanValue = value.replace(/[^\d.]/g, '');
    
    if (!adjustManually && cleanValue) {
      const monthly = parseFloat(cleanValue) || 0;
      const annual = Math.round(monthly * 12);
      const netMonthly = calculateNetFromGrossAnnual(annual);
      
      updateFormData({
        gross_annual_income: annual.toString(),
        gross_monthly_income: cleanValue,
        monthly_income: netMonthly.toString()
      });
    } else {
      updateFormData({ gross_monthly_income: cleanValue });
    }
  };

  const handleNetMonthlyChange = (value) => {
    const cleanValue = value.replace(/[^\d.]/g, '');
    
    if (!adjustManually && cleanValue) {
      const netMonthly = parseFloat(cleanValue) || 0;
      const grossAnnual = estimateGrossFromNet(netMonthly);
      const grossMonthly = Math.round(grossAnnual / 12);
      
      updateFormData({
        gross_annual_income: grossAnnual.toString(),
        gross_monthly_income: grossMonthly.toString(),
        monthly_income: cleanValue
      });
    } else {
      updateFormData({ monthly_income: cleanValue });
    }
  };

  const hasIncome = formData.monthly_income || formData.gross_annual_income || formData.gross_monthly_income;

  const handleMortgageRentChange = (value) => {
    const cleanValue = value.replace(/[^\d.]/g, '');
    updateFormData({ monthly_mortgage_rent: cleanValue });
  };

  return (
    <div className="space-y-6" data-testid="income-step">
      <div className="text-center">
        <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Your Income
        </h2>
        <p className="text-slate-600 hidden md:block">Enter your income — the other fields auto-calculate</p>
      </div>

      <div
        className="max-w-md mx-auto bg-blue-50 border border-blue-200 rounded-md px-4 py-3 text-xs text-blue-900"
        data-testid="income-clarity-hint"
      >
        <strong>Tip:</strong> Annual = full year before tax. Monthly fields are{' '}
        <strong>per month</strong>. Don&apos;t enter a yearly figure in a monthly box —
        that&apos;s the #1 reason a proposal gets declined.
      </div>

      <div className="max-w-md mx-auto space-y-6">
        <div className="flex items-center space-x-2 justify-end">
          <Checkbox
            id="adjust-manually"
            checked={adjustManually}
            onCheckedChange={setAdjustManually}
          />
          <Label htmlFor="adjust-manually" className="text-sm text-slate-600 cursor-pointer">
            Adjust income manually?
          </Label>
        </div>

        <div>
          <Label htmlFor="gross_annual_income" className="text-sm font-medium text-slate-700 mb-1 block">
            Annual Income (full year, before tax)
          </Label>
          <p className="text-xs text-slate-500 mb-2">
            e.g. £30,000 — your gross salary <strong>per year</strong>
          </p>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 text-lg font-medium">£</span>
            <Input
              id="gross_annual_income"
              placeholder="e.g. 30000"
              value={formData.gross_annual_income || ''}
              onChange={(e) => handleGrossAnnualChange(e.target.value)}
              className="h-14 text-xl pl-8"
              data-testid="gross-annual-income-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="gross_monthly_income" className="text-sm font-medium text-slate-700 mb-1 block">
              Monthly (before tax)
            </Label>
            <p className="text-xs text-slate-500 mb-2">Per month, gross</p>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">£</span>
              <Input
                id="gross_monthly_income"
                placeholder="0"
                value={formData.gross_monthly_income || ''}
                onChange={(e) => handleGrossMonthlyChange(e.target.value)}
                className="h-12 text-lg pl-7"
                data-testid="gross-monthly-income-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="monthly_income" className="text-sm font-medium text-slate-700 mb-1 block">
              Take-home (per month)
            </Label>
            <p className="text-xs text-slate-500 mb-2">After tax &amp; NI</p>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">£</span>
              <Input
                id="monthly_income"
                placeholder="0"
                value={formData.monthly_income || ''}
                onChange={(e) => handleNetMonthlyChange(e.target.value)}
                className="h-12 text-lg pl-7"
                data-testid="monthly-income-input"
              />
            </div>
          </div>
        </div>

        {!adjustManually && (
          <p className="text-xs text-slate-500 text-center">
            Calculated using UK PAYE tax + National Insurance rates. Check &quot;Adjust manually&quot; to edit independently.
          </p>
        )}

        {!hasIncome && (
          <p className="text-sm text-amber-600 text-center">
            Please enter at least one income figure
          </p>
        )}

        {(() => {
          // Sanity bands — soft amber nudges for figures that look like the classic
          // "annual entered in monthly" finger-slip or a missing digit. Non-blocking.
          const annual = parseFloat(formData.gross_annual_income) || 0;
          const grossMonthly = parseFloat(formData.gross_monthly_income) || 0;
          const netMonthly = parseFloat(formData.monthly_income) || 0;
          const warnings = [];
          if (annual > 0 && annual < 5000) {
            warnings.push(`Annual income of £${annual.toLocaleString('en-GB')} looks low — did you mean to enter a monthly figure?`);
          }
          if (annual > 500000) {
            warnings.push(`Annual income of £${annual.toLocaleString('en-GB')} looks unusually high — please double-check.`);
          }
          if (grossMonthly > 20000) {
            warnings.push(`Gross monthly of £${grossMonthly.toLocaleString('en-GB')} looks high — did you accidentally enter the annual figure here?`);
          }
          if (netMonthly > 20000) {
            warnings.push(`Take-home monthly of £${netMonthly.toLocaleString('en-GB')} looks high — did you accidentally enter the annual figure here?`);
          }
          if (warnings.length === 0) return null;
          return (
            <div className="mt-2 space-y-1" data-testid="income-sanity-warnings">
              {warnings.map((w, i) => (
                <p key={i} className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  ⚠ {w}
                </p>
              ))}
            </div>
          );
        })()}

        {/* Monthly Mortgage / Rent — applicant's SHARE, not household total */}
        <div className="border-t border-slate-200 pt-6">
          <Label htmlFor="monthly_mortgage_rent" className="text-sm font-medium text-slate-700 mb-1 block">
            Your share of monthly rent / mortgage
          </Label>
          <p className="text-xs text-slate-500 mb-2">
            If split with a partner or housemate, enter YOUR share only. Enter £0 if owned outright or if you don&apos;t contribute.
          </p>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">£</span>
            <Input
              id="monthly_mortgage_rent"
              placeholder="0.00"
              value={formData.monthly_mortgage_rent || ''}
              onChange={(e) => handleMortgageRentChange(e.target.value)}
              className="h-12 text-lg pl-7"
              data-testid="monthly-mortgage-rent-input"
            />
          </div>
        </div>
        <AdditionalIncomeSection />
      </div>
    </div>
  );
};

export default IncomeStep;
